import type { PublicSummary } from "../../contracts/src/index.ts";

const PUBLIC_SUMMARY_CACHE_VERSION = 1;
const PUBLIC_SUMMARY_CACHE_PREFIX = "hq:public-summary";

interface CachedPublicSummary {
  version: typeof PUBLIC_SUMMARY_CACHE_VERSION;
  summary: PublicSummary;
}

function cacheKey(apiBaseUrl: string, accountId: string): string {
  const scope = `${apiBaseUrl.replace(/\/$/, "")}|${accountId}`;
  return `${PUBLIC_SUMMARY_CACHE_PREFIX}:v${PUBLIC_SUMMARY_CACHE_VERSION}:${encodeURIComponent(scope)}`;
}

function isPublicSummary(value: unknown, accountId: string): value is PublicSummary {
  if (!value || typeof value !== "object") return false;
  const summary = value as Partial<PublicSummary>;
  return (
    summary.accountId === accountId &&
    typeof summary.currency === "string" &&
    typeof summary.balance === "number" &&
    Number.isFinite(summary.balance) &&
    typeof summary.equity === "number" &&
    Number.isFinite(summary.equity) &&
    typeof summary.lastUpdatedAt === "number" &&
    Number.isFinite(summary.lastUpdatedAt) &&
    Boolean(summary.itdCashFlow) &&
    typeof summary.itdCashFlow === "object"
  );
}

export function readLastKnownGoodSummary(
  storage: Pick<Storage, "getItem">,
  apiBaseUrl: string,
  accountId: string,
): PublicSummary | null {
  try {
    const raw = storage.getItem(cacheKey(apiBaseUrl, accountId));
    if (!raw) return null;
    const cached = JSON.parse(raw) as Partial<CachedPublicSummary>;
    if (
      cached.version !== PUBLIC_SUMMARY_CACHE_VERSION ||
      !isPublicSummary(cached.summary, accountId)
    ) {
      return null;
    }
    return cached.summary;
  } catch {
    return null;
  }
}

export function writeLastKnownGoodSummary(
  storage: Pick<Storage, "setItem">,
  apiBaseUrl: string,
  accountId: string,
  summary: PublicSummary,
): void {
  if (!isPublicSummary(summary, accountId)) return;
  try {
    const cached: CachedPublicSummary = {
      version: PUBLIC_SUMMARY_CACHE_VERSION,
      summary,
    };
    storage.setItem(cacheKey(apiBaseUrl, accountId), JSON.stringify(cached));
  } catch {
    // Storage can be unavailable or full. Live data must still render normally.
  }
}
